import { createMap } from '@concept3d/maps-web';
import { createModelsPlugin } from '@concept3d/maps-models';
import { handleMapboxTransformRequest } from './mapboxTransform';
import { isDefaultVisibleOverlay } from '../../utils/overlays';
import { resetPlacedModels } from './models';

const C3D_MAPBOX_STYLE = 'mapbox://styles/concept3d/';
const SATELLITE_STYLE = 'mapbox://styles/mapbox/satellite-v9';
const SIDEBAR_TRANSITION_MS = 500;
const CAMERA_EASE_DURATION_MS = 500;

const overlays = new Map();
const overlayVisible = new Map();
const mapReadyListeners = new Set();

let map = null;
let createLock = null;
let epoch = 0;
let commandedCamera = null;
const claimedCreates = new WeakSet();
let defaultStyle = null;
let mapHasInitiallyLoaded = false;
let resizeFrameId = null;
let resizeEndTimer = null;

function notifyMapReady() {
  [...mapReadyListeners].forEach((listener) => listener());
  mapReadyListeners.clear();
}

function whenInitiallyLoaded(callback) {
  if (mapHasInitiallyLoaded) {
    callback();
    return;
  }
  mapReadyListeners.add(callback);
}

export function subscribeMapReady(listener) {
  if (mapHasInitiallyLoaded) {
    listener();
    return () => {};
  }
  mapReadyListeners.add(listener);
  return () => mapReadyListeners.delete(listener);
}

function originAsset(fileName) {
  return `${window.location.origin}/${fileName}`;
}

function ensureMapLibreCss() {
  if (document.getElementById('maps-sdk-maplibre-css')) {
    return;
  }
  const link = document.createElement('link');
  link.id = 'maps-sdk-maplibre-css';
  link.rel = 'stylesheet';
  link.href = originAsset('maplibre-gl.css');
  document.head.appendChild(link);
}

function tileStyleUrl(style) {
  if (style && /^(https?:|mapbox:)/.test(style)) {
    return style;
  }
  if (style) {
    return `${C3D_MAPBOX_STYLE}${style}`;
  }
  return `${process.env.REACT_APP_TILE_SOURCE}${process.env.REACT_APP_TILE_STYLE}/style.json`;
}

function atlasTerrainOptions() {
  const base = process.env.REACT_APP_MAPTILER_URL || 'https://api.maptiler.com';
  const style =
    process.env.REACT_APP_MAPTILER_TERRAIN_TILE_STYLE || 'terrain-rgb-v2';
  const key = process.env.REACT_APP_MAPTILER_KEY;
  if (!key) {
    return undefined;
  }
  return {
    url: `${base}/tiles/${style}/tiles.json?key=${key}`,
    tileSize: 512,
  };
}

function toCenter(lngLat) {
  if (Array.isArray(lngLat)) {
    return { lng: Number(lngLat[0]), lat: Number(lngLat[1]) };
  }
  if (lngLat && typeof lngLat === 'object') {
    return { lng: Number(lngLat.lng), lat: Number(lngLat.lat) };
  }
  return { lng: 0, lat: 0 };
}

function overlayKey(id) {
  return String(id);
}

function cancelPendingResize() {
  if (resizeFrameId !== null) {
    cancelAnimationFrame(resizeFrameId);
    resizeFrameId = null;
  }
  if (resizeEndTimer !== null) {
    clearTimeout(resizeEndTimer);
    resizeEndTimer = null;
  }
}

function scheduleMapResize() {
  if (!map) {
    return;
  }
  if (resizeFrameId !== null) {
    return;
  }
  resizeFrameId = requestAnimationFrame(() => {
    resizeFrameId = null;
    map?.resize();
  });
}

async function buildMap({
  container,
  style,
  lngLat,
  zoom,
  pitch,
  bearing,
  enableTerrain,
}) {
  ensureMapLibreCss();
  defaultStyle = tileStyleUrl(style);
  const center = toCenter(lngLat);
  const terrain = atlasTerrainOptions();
  const created = await createMap({
    container,
    center,
    zoom: Number(zoom) || 0,
    bearing: Number(bearing) || 0,
    pitch: typeof pitch === 'number' ? pitch : 45,
    mapType: 'roadmap',
    provider: 'maplibre',
    maplibre: {
      style: defaultStyle,
      satelliteStyle: SATELLITE_STYLE,
      accessToken: process.env.REACT_APP_MAPBOX_KEY,
      workerUrl: originAsset('maplibre-gl-worker.mjs'),
      transformRequest: (url, resourceType) =>
        handleMapboxTransformRequest(
          url,
          resourceType,
          process.env.REACT_APP_MAPBOX_KEY
        ),
      ...(terrain ? { terrain } : {}),
    },
    plugins: {
      models: createModelsPlugin(),
    },
  });
  if (enableTerrain && terrain) {
    try {
      created.terrain.enable(1);
    } catch (error) {
      console.error('Cannot enable terrain:', error);
    }
  }
  return created;
}

export async function destroyMap() {
  epoch += 1;
  cancelPendingResize();
  resetPlacedModels();
  overlays.clear();
  overlayVisible.clear();
  commandedCamera = null;
  const current = map;
  const lock = createLock;
  map = null;
  createLock = null;
  mapHasInitiallyLoaded = false;
  if (current) {
    await current.destroy();
    return;
  }
  if (lock) {
    claimedCreates.add(lock);
    try {
      const created = await lock;
      await created.destroy();
    } catch {
      // create failed; nothing to destroy
    }
  }
}

export async function initMap(options) {
  if (createLock || map) {
    await destroyMap();
  }
  const generation = epoch;
  const pending = buildMap(options);
  createLock = pending;
  try {
    const created = await pending;
    if (generation !== epoch || createLock !== pending) {
      if (!claimedCreates.has(pending)) {
        try {
          await created?.destroy?.();
        } catch {
          // discarded create; teardown failure leaves the newer map in place
        }
      }
      return null;
    }
    map = created;
    mapHasInitiallyLoaded = true;
    notifyMapReady();
    return map;
  } catch (error) {
    if (createLock === pending) {
      createLock = null;
    }
    if (generation === epoch) {
      map = null;
      mapHasInitiallyLoaded = false;
    }
    throw error;
  }
}

export function getMap() {
  return map;
}

export function setMapType(type) {
  if (!map) {
    return;
  }
  map.setMapType(type === 'satellite' ? 'satellite' : 'roadmap');
}

export function getMapType() {
  if (!map) {
    return 'roadmap';
  }
  return map.getMapType();
}

export function addTileLayer(data, options = {}) {
  if (!map) {
    return;
  }
  const id = overlayKey(data.id);
  const existing = overlays.get(id) ?? map.layers.get?.(id);
  const visible =
    typeof options.visible === 'boolean'
      ? options.visible
      : isDefaultVisibleOverlay(data);
  overlayVisible.set(id, visible);
  if (existing) {
    overlays.set(id, existing);
    map.layers.setVisible(existing, visible);
    return existing;
  }
  const url = `${process.env.REACT_APP_ASSETS}/assets/${data.map}/${data.url}/{z}/{x}/{y}`;
  const handle = map.layers.addTileOverlay({
    id,
    urlTemplate: url,
    scheme: 'tms',
    maxZoom: 22,
    visible,
  });
  overlays.set(id, handle);
  return handle;
}

export function toggleTileLayer(id) {
  const key = overlayKey(id);
  const handle = overlays.get(key);
  if (!handle || !map) {
    return false;
  }
  const nextVisible = overlayVisible.get(key) !== true;
  map.layers.setVisible(handle, nextVisible);
  overlayVisible.set(key, nextVisible);
  return nextVisible;
}

export function getTileLayer(id) {
  return overlays.get(overlayKey(id)) ?? map?.layers.get?.(overlayKey(id));
}

export function createMarker(className, callBack, draggable, name) {
  if (!map) {
    return null;
  }
  const a11yName = name || 'Map marker';
  const interactive = typeof callBack === 'function';
  const handle = map.markers.add({
    kind: 'html',
    position: { lng: 0, lat: 0 },
    html: `<div class="marker ${className || ''}"></div>`,
    draggable: Boolean(draggable),
    a11y: {
      name: a11yName,
      ...(interactive ? { role: 'button' } : {}),
    },
  });
  let clickOff = () => {};
  if (typeof callBack === 'function') {
    clickOff = map.on('click', (event) => {
      if (event.marker && event.marker.id === handle.id) {
        callBack(event);
      }
    });
  }
  return {
    handle,
    setLngLat(pair) {
      const lng = Array.isArray(pair) ? Number(pair[0]) : Number(pair.lng);
      const lat = Array.isArray(pair) ? Number(pair[1]) : Number(pair.lat);
      map.markers.setPose(handle, { position: { lng, lat } });
    },
    addTo() {
      map.markers.setVisible(handle, true);
    },
    hide() {
      map.markers.setVisible(handle, false);
    },
    remove() {
      clickOff();
      map.markers.remove(handle);
    },
    setDraggable(value) {
      map.markers.update(handle, { draggable: Boolean(value) });
    },
    getLngLat() {
      return map.markers.getPosition(handle);
    },
    on(type, listener) {
      if (type === 'dragstart') {
        return map.markers.onDragStart((event) => {
          if (event.marker.id === handle.id) listener(event);
        });
      }
      if (type === 'drag') {
        return map.markers.onDrag((event) => {
          if (event.marker.id === handle.id) listener(event);
        });
      }
      if (type === 'dragend') {
        return map.markers.onDragEnd((event) => {
          if (event.marker.id === handle.id) listener(event);
        });
      }
      return () => {};
    },
  };
}

export function resizeMap() {
  whenInitiallyLoaded(() => {
    if (!map) {
      return;
    }
    cancelPendingResize();
    scheduleMapResize();
    resizeEndTimer = setTimeout(() => {
      resizeEndTimer = null;
      scheduleMapResize();
    }, SIDEBAR_TRANSITION_MS);
  });
}

function liveCamera() {
  return map.camera.getState();
}

function commandedCameraOrLive() {
  const live = liveCamera();
  if (!commandedCamera) {
    return live;
  }
  return { ...live, ...commandedCamera };
}

export function panTo(latLng, zoom, pitch) {
  whenInitiallyLoaded(() => {
    if (!map) {
      return;
    }
    const patch = { center: toCenter(latLng) };
    if (typeof zoom === 'number') {
      patch.zoom = zoom;
    }
    if (typeof pitch === 'number') {
      patch.pitch = pitch;
    }
    easeCamera(patch);
  });
}

function easeCamera(patch) {
  commandedCamera = { ...commandedCameraOrLive(), ...patch };
  const target = commandedCamera;
  Promise.resolve(
    map.camera.easeTo(target, { durationMs: CAMERA_EASE_DURATION_MS })
  ).finally(() => {
    if (commandedCamera === target) {
      commandedCamera = null;
    }
  });
}

export function rotateTo(options) {
  if (!map) {
    return;
  }
  if (typeof options === 'number') {
    easeCamera({ bearing: options });
    return;
  }
  if (!options || typeof options !== 'object') {
    return;
  }
  const { lat, lng, zoom, pitch, bearing } = options;
  const patch = {};
  if (typeof lat === 'number' && typeof lng === 'number') {
    patch.center = { lat, lng };
  }
  if (typeof zoom === 'number') patch.zoom = zoom;
  if (typeof pitch === 'number') patch.pitch = pitch;
  if (typeof bearing === 'number') patch.bearing = bearing;
  easeCamera(patch);
}

export function zoomTo(zoom) {
  if (!map) {
    return;
  }
  easeCamera({ zoom });
}

export function setBearing(bearing) {
  if (!map) {
    return;
  }
  map.camera.setState({ bearing });
}

export function contains(lat, lng) {
  if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return map.camera.contains({ lat, lng });
}

export function getCenter() {
  if (!map) {
    return undefined;
  }
  return commandedCameraOrLive().center;
}

export function getZoom() {
  if (!map) {
    return undefined;
  }
  return commandedCameraOrLive().zoom;
}

export function getPitch() {
  if (!map) {
    return undefined;
  }
  return commandedCameraOrLive().pitch;
}

export function getBearing() {
  if (!map) {
    return undefined;
  }
  return commandedCameraOrLive().bearing;
}

if (process.env.NODE_ENV === 'development') {
  window.rotateTo = rotateTo;
  window.getMap = getMap;
}
